"use client";

import { Logo } from "@/components/common/logo";
import { MobileMenu } from "@/components/common/mobile-menu";
import { AuthGuard } from "@/components/guards/auth-guard";
import { Button } from "@/components/ui/button";
import Heart from "@repo/icons/react/Heart";
import Search from "@repo/icons/react/Search";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useProfessionalDesk } from "@/hooks/use-professional-desk";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ThemeToggle } from "@/components/common/theme-toggle";
import { useEffect, useRef, useState } from "react";
import { MainNav } from "./main-nav";

import dynamic from "next/dynamic";

const UserMenu = dynamic(() =>
  import("./user-menu").then((module) => module.UserMenu),
);
const ChatMenuItem = dynamic(() =>
  import("./chat-menu-item").then((module) => module.ChatMenuItem),
);
const HeaderNewListing = dynamic(() =>
  import("./header-new-listing").then((module) => module.HeaderNewListing),
);

export function TheHeader() {
  const t = useTranslations();
  const pathname = usePathname();
  const { user, loaded } = useCurrentUser();
  const desk = useProfessionalDesk();
  const [isScrolled, setIsScrolled] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const rafRef = useRef<number | null>(null);

  const isLoggedIn = user && loaded;
  const isAgent = desk.marketplace.rememberedPath
    ? Boolean(desk.marketplace.data?.isBroker)
    : !!user?.isAgent;
  const pathSegments = pathname.split("/").filter(Boolean);
  const isBlogArticlePath =
    pathSegments[0] === "blog" && pathSegments.length === 2;

  useEffect(() => {
    const updateScrollState = () => {
      const next = window.scrollY > 10;
      setIsScrolled((prev) => (prev === next ? prev : next));
    };

    const onScroll = () => {
      if (rafRef.current != null) {
        return;
      }
      rafRef.current = window.requestAnimationFrame(() => {
        updateScrollState();
        rafRef.current = null;
      });
    };

    updateScrollState();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isBlogArticlePath) {
      setReadingProgress(0);
      return;
    }

    const onScroll = () => {
      const scrollTop = window.scrollY;
      const maxScroll = document.body.scrollHeight - window.innerHeight;

      if (maxScroll <= 0) {
        setReadingProgress(0);
        return;
      }

      const pct = Math.min(100, Math.max(0, (scrollTop / maxScroll) * 100));
      setReadingProgress(pct);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [isBlogArticlePath]);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-[1000] h-14 md:h-16 border-b border-border/70 transition-all duration-300",
        "bg-background/95 backdrop-blur-[12px] supports-[backdrop-filter]:bg-background/88",
        isScrolled &&
          "bg-background/85 backdrop-blur-[20px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-[0_1px_3px_rgba(0,0,0,0.25)]",
      )}
    >
      <div className="container mx-auto h-full">
        <div className="flex h-full items-center justify-between px-4 lg:px-3">
          <div className="flex items-center gap-6">
            <Link
              href="/"
              className="flex rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label={t("web.a11y.home", { appName: t("common.name") })}
            >
              <Logo size="xs" />
            </Link>

            {isAgent && (
              <div className="hidden lg:inline-flex items-center rounded-full border border-theme-green/30 bg-theme-green-soft px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-theme-emerald">
                Broker
              </div>
            )}

            {isAgent && <HeaderNewListing />}

            <MainNav isAgent={isAgent} />
          </div>

          <div className="hidden lg:flex items-center gap-6">
            <div className="mx-1 h-6 w-px bg-border" />
            <ThemeToggle />
            <AuthGuard title={t("web.topNav.favorites")}>
              {isLoggedIn ? (
                <Link
                  href="/my-favorites"
                  className="inline-flex items-center gap-2 whitespace-nowrap text-[15px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Heart className="h-4 w-4" aria-hidden />
                  {t("web.topNav.favorites")}
                </Link>
              ) : (
                <button
                  type="button"
                  className="inline-flex items-center gap-2 whitespace-nowrap text-[15px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Heart className="h-4 w-4" aria-hidden />
                  {t("web.topNav.favorites")}
                </button>
              )}
            </AuthGuard>

            <AuthGuard title={t("web.topNav.searches")}>
              {isLoggedIn ? (
                <Link
                  href="/my-searches"
                  className="inline-flex items-center gap-2 whitespace-nowrap text-[15px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Search className="h-4 w-4" aria-hidden />
                  {t("web.topNav.searches")}
                </Link>
              ) : (
                <button
                  type="button"
                  className="inline-flex items-center gap-2 whitespace-nowrap text-[15px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Search className="h-4 w-4" aria-hidden />
                  {t("web.topNav.searches")}
                </button>
              )}
            </AuthGuard>

            {isLoggedIn && !desk.isProfessional && (
              <ChatMenuItem label={t("web.topNav.chat")} />
            )}

            {user && loaded && <UserMenu />}

            {!user && loaded && (
              <AuthGuard>
                <Button variant="default" size="sm" className="px-5 text-sm">
                  {t("web.topNav.login")}
                </Button>
              </AuthGuard>
            )}
          </div>

          <div className="flex lg:hidden gap-2 items-center">
            <MobileMenu />
          </div>
        </div>
      </div>

      {isBlogArticlePath ? (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[1001] h-[2px] bg-transparent"
          aria-hidden
        >
          <div
            className="h-full bg-[linear-gradient(90deg,success_0%,theme-blue_100%)] transition-[width] duration-150"
            style={{ width: `${readingProgress}%` }}
          />
        </div>
      ) : null}
    </header>
  );
}

TheHeader.displayName = "TheHeader";
